import {
  WEBSITE_CANVAS_MAX_REGIONS,
  WEBSITE_CANVAS_REGION_LABEL_MAX_LENGTH,
  type WebsiteCanvasRegionChrome,
  type WebsiteEditRegion,
  type WebsiteRegionLabelId,
  type WebsiteSlotSchema,
} from '@dukanti/shared';
import type { WebsiteT } from '../hooks/use-website-t';

const REGION = 'admin.websiteTemplates.region';
const SLOT = 'admin.websiteTemplates.slot';

export const regionLabelKey = (labelId: WebsiteRegionLabelId): string =>
  `${REGION}.${labelId}.label`;
export const regionEditKey = (labelId: WebsiteRegionLabelId): string => `${REGION}.${labelId}.edit`;

function withOrdinal(t: WebsiteT, label: string, ordinal: number | undefined): string {
  return ordinal === undefined ? label : t(`${SLOT}.ordinal`, { label, n: ordinal });
}

/** "Hero text 2": what names a region in lists and in the dialog. */
export function regionName(t: WebsiteT, region: WebsiteEditRegion): string {
  return withOrdinal(t, t(regionLabelKey(region.labelId)), region.ordinal);
}

/** "Edit hero text 2": the accessible name of the marker and the title of the dialog. */
export function regionEditLabel(t: WebsiteT, region: WebsiteEditRegion): string {
  return withOrdinal(t, t(regionEditKey(region.labelId)), region.ordinal);
}

/** "Main heading 2": the label of one field of a region. */
export function slotFieldLabel(
  t: WebsiteT,
  slot: Pick<WebsiteSlotSchema, 'semantic' | 'ordinal'>,
): string {
  return withOrdinal(t, t(`${SLOT}.${slot.semantic}`), slot.ordinal);
}

function clip(label: string): string {
  const points = Array.from(label);
  return points.length <= WEBSITE_CANVAS_REGION_LABEL_MAX_LENGTH
    ? label
    : points.slice(0, WEBSITE_CANVAS_REGION_LABEL_MAX_LENGTH).join('');
}

/**
 * The regions of one page as the canvas gets them, each with its finished
 * accessible name in the Admin language. The canvas renders the label as text
 * and never translates it.
 */
export function toCanvasRegionChrome(
  t: WebsiteT,
  regions: readonly WebsiteEditRegion[],
): WebsiteCanvasRegionChrome[] {
  return regions.slice(0, WEBSITE_CANVAS_MAX_REGIONS).map((region) => ({
    regionId: region.regionId,
    label: clip(regionEditLabel(t, region)),
  }));
}
